import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Bot, 
  Send, 
  User, 
  Loader2,
  Sparkles,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatBot() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hello ${user?.name || 'there'}! I'm your Privacy Protection Assistant. I can help you with:\n\n• Understanding digital surveillance threats\n• Step-by-step guides to disable tracking\n• VPN and privacy tool recommendations\n• Privacy laws and your rights\n• Securing your accounts and data\n\nWhat would you like to know?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to get response. Please try again.';
      toast.error(errorMessage);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Please try again or rephrase your question.',
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestedQuestions = [
    'How do I reset my advertising ID?',
    'What VPN do you recommend?',
    'How does Fog Reveal track people?',
    'How to enable 2FA everywhere?',
  ];

  const handleSuggestedQuestion = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: `Starting a new conversation. How can I help you protect your privacy today?`
    }]);
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="chatbot-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-[#00FF94]" />
              </div>
              Privacy AI Assistant
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Ask anything about digital privacy and protection
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={startNewChat}
            className="border-[#262626] text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="new-chat-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            New Chat
          </Button>
        </div>

        {/* Chat Container */}
        <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl overflow-hidden">
          {/* Messages Area */}
          <ScrollArea className="h-[500px] p-6" ref={scrollRef}>
            <div className="space-y-6">
              {messages.map((message, index) => (
                <div 
                  key={index}
                  className={`flex gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                  data-testid={`chat-message-${index}`}
                >
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-[#3B82F6]/10 border border-[#3B82F6]/20' 
                      : message.error 
                        ? 'bg-red-500/10 border border-red-500/20'
                        : 'bg-[#00FF94]/10 border border-[#00FF94]/20'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="w-4 h-4 text-[#3B82F6]" />
                    ) : message.error ? (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <Sparkles className="w-4 h-4 text-[#00FF94]" />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block text-left max-w-[85%] px-4 py-3 rounded-xl ${
                      message.role === 'user'
                        ? 'bg-[#3B82F6]/10 border border-[#3B82F6]/20 text-white'
                        : message.error
                          ? 'bg-red-500/5 border border-red-500/20 text-gray-300'
                          : 'bg-[#171717] border border-[#262626] text-gray-300'
                    }`}>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="flex gap-4" data-testid="chat-loading">
                  <div className="w-8 h-8 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#00FF94]" />
                  </div>
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#171717] border border-[#262626] rounded-xl">
                    <Loader2 className="w-4 h-4 text-[#00FF94] animate-spin" />
                    <span className="text-sm text-gray-400">Thinking...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Suggested Questions */}
          {messages.length <= 2 && (
            <div className="px-6 py-3 border-t border-[#262626] bg-[#050505]">
              <p className="text-xs text-gray-500 mb-2">Suggested questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedQuestion(question)}
                    className="px-3 py-1.5 text-xs bg-[#171717] border border-[#262626] rounded-full text-gray-400 hover:text-white hover:border-[#00FF94]/30 transition-colors"
                    data-testid={`suggested-question-${index}`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={sendMessage} className="p-4 border-t border-[#262626]">
            <div className="flex gap-3">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about privacy protection..."
                className="flex-1 bg-[#171717] border-[#262626] text-white placeholder:text-gray-600 focus:border-[#00FF94] focus:ring-[#00FF94]/20"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={!input.trim() || loading}
                className="btn-primary px-4"
                data-testid="chat-send-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-600 mt-2 text-center">
              Powered by AI • Responses are for educational purposes
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
